import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import { useTutorial } from '../../context/TutorialContext';
import { useGlobalAudio } from '../../context/GlobalAudioContext';
import SpeakingLogo from '../ui/SpeakingLogo';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * TutorialOverlay — lorsqu'un tutoriel est actif :
 *  - navigue vers la page de l'étape courante
 *  - cherche l'élément cible via data-tutorial-id (ou CSS selector)
 *  - affiche un spotlight (voile semi-transparent avec découpe)
 *  - affiche une bulle ancrée à l'élément avec texte + logo parlant + Next/Prev/Close
 *  - joue l'audio de narration
 */
const TutorialOverlay = () => {
    const { activeTutorial, currentStep, currentStepIndex, totalSteps,
            nextStep, prevStep, stopTutorial, registerNavigate, playStepAudio } = useTutorial();
    const { isPlaying } = useGlobalAudio();
    const navigate = useNavigate();
    const location = useLocation();
    const [targetRect, setTargetRect] = useState(null);
    const lastPlayedStepRef = useRef(null);

    // Exposer navigate au context
    useEffect(() => {
        registerNavigate(navigate);
    }, [registerNavigate, navigate]);

    // Navigation automatique vers la page de l'étape
    useEffect(() => {
        if (!currentStep) return;
        if (currentStep.pageroute && location.pathname !== currentStep.pageroute) {
            navigate(currentStep.pageroute, { replace: false });
        }
        // navigate est stable via useNavigate, on l'exclut des deps pour éviter les boucles
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep?.pageroute, currentStep?.id, location.pathname]);

    // Localiser l'élément cible + jouer l'audio
    const locateTarget = useCallback(() => {
        if (!currentStep) {
            setTargetRect(null);
            return;
        }
        const selector = currentStep.targetselector;
        if (!selector) {
            setTargetRect(null);
            return;
        }
        let el = null;
        if (selector.startsWith('.') || selector.startsWith('#') || selector.includes('[')) {
            try { el = document.querySelector(selector); } catch { /* ignore */ }
        } else {
            el = document.querySelector(`[data-tutorial-id="${selector}"]`);
        }
        if (el) {
            const rect = el.getBoundingClientRect();
            setTargetRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
            });
            try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* ignore */ }
        } else {
            setTargetRect(null);
        }
    }, [currentStep]);

    useEffect(() => {
        if (!currentStep) return;
        // Polling pour attendre que l'élément soit monté (max 2s)
        let tries = 0;
        const interval = setInterval(() => {
            locateTarget();
            tries++;
            if (tries > 20) clearInterval(interval);
        }, 100);
        const onResize = () => locateTarget();
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onResize, true);
        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onResize, true);
        };
    }, [currentStep, locateTarget]);

    // Jouer l'audio quand on entre dans une nouvelle étape
    useEffect(() => {
        if (!currentStep) return;
        const stepKey = `${activeTutorial?.uuid}-${currentStepIndex}`;
        if (lastPlayedStepRef.current === stepKey) return;
        lastPlayedStepRef.current = stepKey;
        if (currentStep.audioPath) {
            playStepAudio(`${FILES_BASE_URL}/${currentStep.audioPath}`, { anchor: 'bubble' });
        }
    }, [currentStep, currentStepIndex, activeTutorial, playStepAudio]);

    if (!activeTutorial || !currentStep) return null;

    // Si pas d'élément cible trouvé : bulle centrée
    const useFallback = !targetRect;

    // Calcul position de la bulle
    const bubbleStyle = useFallback
        ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
        : computeBubblePosition(targetRect, currentStep.tooltipposition || 'top');

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'none' }}>
            {/* Spotlight : voile + trou sur l'élément cible + border pulsante */}
            {targetRect && (
                <>
                    <style>{`
                        @keyframes cabazaHaloPulse {
                            0%, 100% { opacity: 0.9; stroke-width: 2.5; }
                            50%      { opacity: 0.5; stroke-width: 4; }
                        }
                    `}</style>
                    <svg style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', pointerEvents: 'none' }}>
                        <defs>
                            <mask id="cabaza-tutorial-mask">
                                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                <rect
                                    x={targetRect.left - 6}
                                    y={targetRect.top - 6}
                                    width={targetRect.width + 12}
                                    height={targetRect.height + 12}
                                    rx="8"
                                    fill="black"
                                />
                            </mask>
                            <linearGradient id="cabaza-halo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#8B5CF6" />
                                <stop offset="100%" stopColor="#EC4899" />
                            </linearGradient>
                        </defs>
                        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#cabaza-tutorial-mask)" style={{ transition: 'all 0.3s ease' }} />
                        <rect
                            x={targetRect.left - 6}
                            y={targetRect.top - 6}
                            width={targetRect.width + 12}
                            height={targetRect.height + 12}
                            rx="8"
                            fill="none"
                            stroke="url(#cabaza-halo-grad)"
                            style={{
                                animation: 'cabazaHaloPulse 1.6s ease-in-out infinite',
                                transition: 'x 0.3s, y 0.3s, width 0.3s, height 0.3s'
                            }}
                        />
                    </svg>
                </>
            )}

            {/* Bulle + logo parlant + contrôles */}
            <div
                style={{
                    position: 'fixed',
                    ...bubbleStyle,
                    pointerEvents: 'auto',
                    maxWidth: 360,
                    backgroundColor: '#8B5CF6',
                    color: 'white',
                    padding: 16,
                    borderRadius: 16,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start'
                }}
            >
                <SpeakingLogo size={56} isSpeaking={isPlaying} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>
                        Étape {currentStepIndex + 1} / {totalSteps}
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.4, marginBottom: 12 }}>
                        {currentStep.bubbleText || '(Pas de texte)'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                        <button
                            onClick={() => stopTutorial(false)}
                            style={{
                                background: 'rgba(255,255,255,0.15)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 8,
                                padding: '6px 10px',
                                cursor: 'pointer',
                                fontSize: 12,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            <X size={14} /> Quitter
                        </button>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button
                                onClick={prevStep}
                                disabled={currentStepIndex === 0}
                                style={{
                                    background: 'rgba(255,255,255,0.15)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '6px 10px',
                                    cursor: currentStepIndex === 0 ? 'not-allowed' : 'pointer',
                                    opacity: currentStepIndex === 0 ? 0.5 : 1,
                                    fontSize: 12,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                onClick={nextStep}
                                style={{
                                    background: 'white',
                                    color: '#8B5CF6',
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4
                                }}
                            >
                                {currentStepIndex === totalSteps - 1 ? 'Terminer' : 'Suivant'}
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

function computeBubblePosition(rect, position) {
    const GAP = 16;
    const BUBBLE_W = 360; // max estimation
    const BUBBLE_H = 140;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top, left;
    switch (position) {
        case 'bottom':
            top = rect.top + rect.height + GAP;
            left = rect.left + rect.width / 2 - BUBBLE_W / 2;
            break;
        case 'left':
            top = rect.top + rect.height / 2 - BUBBLE_H / 2;
            left = rect.left - BUBBLE_W - GAP;
            break;
        case 'right':
            top = rect.top + rect.height / 2 - BUBBLE_H / 2;
            left = rect.left + rect.width + GAP;
            break;
        case 'top':
        default:
            top = rect.top - BUBBLE_H - GAP;
            left = rect.left + rect.width / 2 - BUBBLE_W / 2;
            break;
    }
    // Clamp dans le viewport
    top = Math.max(12, Math.min(vh - BUBBLE_H - 12, top));
    left = Math.max(12, Math.min(vw - BUBBLE_W - 12, left));
    return { top, left };
}

export default TutorialOverlay;
